import type { ReactNode } from 'react'

import { cn } from '@/ui/lib/utils'

export interface FieldRowProps {
  label: string
  /** `Required` / `Optional` and other guidance — never baked into `label` (spec 010 §3). */
  hint?: string
  /** Associates the label with a single control's `id`. */
  htmlFor?: string
  /** `start` for multi-line controls (textarea); `baseline` (default) otherwise. */
  align?: 'baseline' | 'start'
  children: ReactNode
  className?: string
}

/**
 * Station Kit v2's one-and-only form-layout object (spec 010 §3). A 156px
 * label column (name + optional hint, stacked) next to a `flex:1 1 300px`
 * control column. Below ~500px of panel width the control wraps under the
 * label — that is the entire responsive story, no media query. Rows carry a
 * hairline `--ink-14` bottom divider except the last one in a panel
 * (`last:border-b-0`).
 *
 * The `Required`/`Optional` word lives in `hint`, never in `label` — spec 010
 * §3 deletes every `(REQUIRED)` string in the codebase, and this component is
 * where that rule is enforced structurally.
 */
export function FieldRow({ label, hint, htmlFor, align = 'baseline', children, className }: FieldRowProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap gap-x-5 gap-y-1 border-b border-ink-14 py-[11px] last:border-b-0',
        align === 'baseline' ? 'items-baseline' : 'items-start',
        className,
      )}
    >
      <label htmlFor={htmlFor} className="flex shrink-0 basis-[156px] flex-col gap-0.5">
        <span className="font-sans text-[11px] font-semibold tracking-[0.2em] uppercase">{label}</span>
        {hint && <span className="font-mono text-[9px] tracking-[0.1em] text-ink-50 uppercase">{hint}</span>}
      </label>
      <div className="min-w-0 flex-1 basis-[300px]">{children}</div>
    </div>
  )
}

export interface FieldRowFooterProps {
  /** Optional left-aligned status line, e.g. `Missing: identifier, model.` (spec 010 §3/§5). */
  status?: ReactNode
  children: ReactNode
  className?: string
}

/**
 * The action footer that closes a `SectionPanel` body (spec 010 §3). A
 * top-hairline (`--ink-20`) row of right-aligned buttons; when a status line is
 * supplied it sits left on the same row (`space-between`), which is how the
 * creation forms show `Missing: …` beside a disabled primary (§5).
 */
export function FieldRowFooter({ status, children, className }: FieldRowFooterProps) {
  return (
    <div
      className={cn(
        'mt-2 flex items-center gap-2 border-t border-ink-20 pt-[14px]',
        status ? 'justify-between' : 'justify-end',
        className,
      )}
    >
      {status && (
        <span className="font-mono text-[10px] tracking-[0.1em] text-ink-50 uppercase">{status}</span>
      )}
      <div className="flex items-center gap-2">{children}</div>
    </div>
  )
}
